import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import _ from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import colors from "assets/theme/base/colors";
import Icon from "@mui/material/Icon";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiSelect from "components/SuiSelect";
import SuiEditor from "components/SuiEditor";
import FormField from "components/FormField";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { Page } from "api";

function NewOrEditPage({ mode }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEditing = !!id;
  const TITLE = t(isEditing ? "Edit Page" : "New Page");
  const history = useHistory();
  const [page, setPage] = useState({
    status: "draft",
  });
  const [pageOriginal, setPageOriginal] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandSeo, setExpandSeo] = useState(true);
  const [showHtml, setShowHtml] = useState(false);
  const [currLang, setCurrLang] = useState("SC");

  const getCurrLangLabel = () => {
    if (currLang === "TC") {
      return t("Traditional Chinese");
    }
    if (currLang === "SC") {
      return t("Simplified Chinese");
    }
    return t("English");
  };

  useEffect(() => {
    if (!isEditing) return;
    (async () => {
      setIsLoading(true);
      try {
        const { data } = await Page.get(id);
        setPage(data);
        setPageOriginal(data);
      } catch (err) {
        console.error(err);
      }
      setIsLoading(false);
    })();
  }, []);

  const readPage = (prop) => {
    if (prop === "css" || prop === "status") {
      return page[prop];
    }
    return page[prop + currLang] || "";
  };
  const readPageOriginal = (prop) => {
    if (prop === "css" || prop === "status") {
      return pageOriginal[prop];
    }
    return pageOriginal[prop + currLang] || "";
  };
  const updatePage = (prop, value) => {
    const n = { ...page };
    if (prop === "css" || prop === "status") {
      n[prop] = value;
    } else {
      n[prop + currLang] = value;
    }
    setPage(n);
  };
  const editorOnChange = (e) => updatePage("content", e);
  const getPageStatusLabel = () => {
    if (page.status === "3") {
      return "Published";
    }
    if (page.status === "2") {
      return "Scheduled";
    }
    return "Draft";
  };

  const save = async () => {
    let error = "";
    if (!isEditing) {
      if (page.title || page.slug) {
        if (!page.title) {
          error = "Missing title";
        } else if (!page.slug) {
          error = "Missing slug";
        }
      } else if (page.titleTC || page.slugTC) {
        if (!page.titleTC) {
          error = "Missing title";
        } else if (!page.slugTC) {
          error = "Missing slug";
        }
      } else if (page.titleSC || page.slugSC) {
        if (!page.titleSC) {
          error = "Missing title";
        } else if (!page.slugSC) {
          error = "Missing slug";
        }
      } else {
        error = "Missing title";
      }
    }
    if (error) {
      Swal.fire(t(error));
    } else {
      try {
        const data = {};
        [
          "id",
          "title",
          "titleTC",
          "titleSC",
          "slug",
          "slugTC",
          "slugSC",
          "content",
          "contentTC",
          "contentSC",
          "css",
          "metaTitle",
          "metaTitleTC",
          "metaTitleSC",
          "metaKeyword",
          "metaKeywordTC",
          "metaKeywordSC",
          "metaDescription",
          "metaDescriptionTC",
          "metaDescriptionSC",
          "status",
          "schedulePublishedDate",
          "publishedDate",
        ].forEach((v) => {
          data[v] = page[v];
        });
        if (isEditing) {
          await Page.update(data);
        } else {
          await Page.create(data);
        }
        history.goBack();
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox my={1.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <Card style={{ overflow: "visible" }}>
                <SuiBox p={3}>
                  <SuiBox>
                    <SuiBox
                      py={1}
                      style={{
                        float: "right",
                        minWidth: 200,
                      }}
                    >
                      <SuiSelect
                        placeholder="Choose a type"
                        value={{
                          label: getCurrLangLabel(),
                          value: currLang,
                        }}
                        options={[
                          {
                            label: t("Traditional Chinese"),
                            value: "TC",
                          },
                          {
                            label: t("Simplified Chinese"),
                            value: "SC",
                          },
                          {
                            label: t("English"),
                            value: "",
                          },
                        ]}
                        onChange={(option) => {
                          setCurrLang(option.value);
                        }}
                      />
                    </SuiBox>
                    <SuiTypography variant="h5">{TITLE}</SuiTypography>
                    <SuiTypography variant="button" fontWeight="regular" color="text">
                      {t(`${isEditing ? "Edit" : "New"} Page`)}
                    </SuiTypography>
                  </SuiBox>

                  <Divider />
                  {(!isEditing || (isEditing && !isLoading)) && (
                    <Grid item>
                      <FormField
                        label={t("title")}
                        disabled={(isEditing && readPageOriginal("title")) || mode === "view"}
                        value={readPage("title")}
                        onChange={(e) => {
                          updatePage("title", e.target.value);
                        }}
                      />
                      <FormField
                        label={t("slug")}
                        disabled={(isEditing && readPageOriginal("slug")) || mode === "view"}
                        value={readPage("slug")}
                        onChange={(e) => {
                          updatePage("slug", e.target.value);
                        }}
                      />
                      <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                        <SuiTypography component="label" variant="caption" fontWeight="bold">
                          {t("Content")}
                        </SuiTypography>
                        &nbsp;&nbsp;
                        <SuiButton
                          iconOnly
                          onClick={() => {
                            setShowHtml(!showHtml);
                          }}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>data_object</Icon>
                        </SuiButton>
                      </SuiBox>
                      {!showHtml && (
                        <SuiBox mb={3}>
                          {currLang === "" && (
                            <SuiEditor
                              readOnly={mode === "view"}
                              value={readPage("content") || ""}
                              onChange={editorOnChange}
                            />
                          )}
                          {currLang === "TC" && (
                            <SuiEditor
                              readOnly={mode === "view"}
                              value={readPage("content") || ""}
                              onChange={editorOnChange}
                            />
                          )}
                          {currLang === "SC" && (
                            <SuiEditor
                              readOnly={mode === "view"}
                              value={readPage("content") || ""}
                              onChange={editorOnChange}
                            />
                          )}
                        </SuiBox>
                      )}
                      {showHtml && (
                        <FormField
                          lable={<></>}
                          placeholder={t("Enter Content")}
                          multiline
                          minRows={5}
                          disabled={mode === "view"}
                          onChange={(e) => {
                            updatePage("content", e.target.value);
                          }}
                          value={readPage("content")}
                        />
                      )}
                      <FormField
                        label="CSS"
                        placeholder={t("Enter CSS")}
                        multiline
                        minRows={5}
                        onChange={(e) => {
                          updatePage("css", e.target.value);
                        }}
                        value={page.css}
                        disabled={mode === "view"}
                      />
                      <SuiBox
                        p={3}
                        py={1.5}
                        style={{
                          background: colors.background.default,
                          margin: "0 -24px",
                          position: "relative",
                          fontSize: 12,
                          fontWeight: "bold",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setExpandSeo(!expandSeo);
                        }}
                      >
                        {t("Search Engine Optimization")}
                        <SuiBox
                          p={2}
                          py={1.5}
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                          }}
                        >
                          <SuiTypography>
                            <Icon sx={{ fontWeight: "bold" }}>
                              {expandSeo ? "expand_less" : "expand_more"}
                            </Icon>
                          </SuiTypography>
                        </SuiBox>
                      </SuiBox>
                      {expandSeo && (
                        <>
                          <FormField
                            label={t("Meta Title")}
                            placeholder={t("Enter Meta Title")}
                            onChange={(e) => {
                              updatePage("metaTitle", e.target.value);
                            }}
                            value={readPage("metaTitle")}
                            disabled={mode === "view"}
                          />
                          <FormField
                            label={t("Meta Keywords")}
                            placeholder={t("Enter Meta Keywords")}
                            multiline
                            minRows={5}
                            onChange={(e) => {
                              updatePage("metaKeyword", e.target.value);
                            }}
                            value={readPage("metaKeyword")}
                            disabled={mode === "view"}
                          />
                          <FormField
                            label={t("Meta Description")}
                            placeholder={t("Enter Meta Description")}
                            multiline
                            minRows={5}
                            onChange={(e) => {
                              updatePage("metaDescription", e.target.value);
                            }}
                            value={readPage("metaDescription")}
                            disabled={mode === "view"}
                          />
                        </>
                      )}
                    </Grid>
                  )}
                </SuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} lg={3}>
              <TableContainer style={{ padding: 5 }}>
                <Table>
                  <TableHead>
                    <SuiBox component="tr" width="max-content" display="block" mb={1.5}>
                      <SuiTypography variant="h5" component="td">
                        {t("Page Settings")}
                      </SuiTypography>
                    </SuiBox>
                    <Divider />
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell style={{ border: 0 }}>
                        <SuiBox style={{ textAlign: "right" }}>
                          <SuiSelect
                            value={{
                              label: t(_.capitalize(getPageStatusLabel())),
                              value: "",
                            }}
                            options={[
                              {
                                label: t("Draft"),
                                value: "1",
                              },
                              // {
                              //   label: t("Scheduled"),
                              //   value: "2",
                              // },
                              {
                                label: t("Published"),
                                value: "3",
                              },
                            ]}
                            onChange={(option) => {
                              updatePage("status", option.value);
                            }}
                            isDisabled={mode === "view"}
                          />
                        </SuiBox>
                        <SuiBox mt={7} mb={3} style={{ textAlign: "right" }}>
                          <SuiBox mt={7} mb={3} style={{ fontSize: 12 }} display="inline">
                            {page.updatedAt && (
                              <>
                                {t("Last edit")}:{" "}
                                {moment(page.updatedAt).format("YYYY-MM-DD hh:mm:ss A")}
                              </>
                            )}
                          </SuiBox>
                          <SuiBox ml={2} display="inline">
                            <SuiButton variant="gradient" color="info" onClick={save}>
                              {t("save")}
                            </SuiButton>
                          </SuiBox>
                        </SuiBox>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </SuiBox>
        <Footer />
      </DashboardLayout>
    </>
  );
}

NewOrEditPage.defaultProps = {
  mode: "view",
};
NewOrEditPage.propTypes = {
  mode: PropTypes.string,
};

export default NewOrEditPage;
